import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Listbox, Combobox, Transition } from '@headlessui/react';
import { useHistory } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  Squares2X2Icon,
  ChevronUpDownIcon,
  CheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { cn } from '../../shared/utils/cn';
import FadeInImage from '../../shared/components/FadeInImage';
import * as API from '../../api';

/**
 * Tafwija Hero — editorial layout with small inline imagery.
 *
 *   ┌──────────────────────────────────┬────────────────────────┐
 *   │  ★ badge                         │                        │
 *   │  Headline                        │  3 small overlapping   │
 *   │  Subtitle                        │  rounded photos +      │
 *   │                                  │  yellow accent disc    │
 *   │  search bar                      │                        │
 *   │  quick chips                     │  small rating card     │
 *   │  inline stats                    │                        │
 *   └──────────────────────────────────┴────────────────────────┘
 *
 * The right column is intentionally light — small thumbnails inline with copy,
 * not a giant grid. Trending experiences live in a separate section below.
 */

// Each slide = one set of 3 photos for the \|/ trio.
// Auto-rotates every ~4 seconds with a cross-fade.
const INLINE_SLIDES = [
  [
    { img: '/storage/events/parapente-a-marrakech-events-partner-85-2023-12-20.jpg',           alt: 'Parapente Marrakech' },
    { img: '/storage/events/balade-a-cheval-essaouira-events-1-2021-03-12.jpeg',               alt: 'Balade à cheval' },
    { img: '/storage/events/activite-de-buggy-a-tanger-events-partner-657-2024-06-07.jpeg',    alt: 'Buggy Tanger' },
  ],
  [
    { img: '/storage/events/activite-de-jet-ski-a-dakhla-events-partner-669-2024-07-01.jpeg',      alt: 'Jet ski Dakhla' },
    { img: '/storage/events/balade-a-chameau-agafay-events-partner-396-2021-11-28.jpeg',           alt: 'Désert Agafay' },
    { img: '/storage/events/activite-de-quad-a-chefchaouen-events-partner-649-2024-05-24.jpeg',    alt: 'Quad Chefchaouen' },
  ],
  [
    { img: '/storage/events/atelier-zellige-events-partner-862-2025-02-06.png',                       alt: 'Atelier Zellige' },
    { img: '/storage/events/2j-1-nuit-marrekch-imlil-ourika-chaque-weekend-events-1-2023-11-13.jpeg', alt: 'Atlas Imlil' },
    { img: '/storage/events/activite-kayak-sale-events-partner-496-2023-04-05.jpeg',                  alt: 'Kayak Salé' },
  ],
];
const SLIDE_INTERVAL_MS = 2800;

const QUICK_CITIES = ['Marrakech', 'Essaouira', 'Chefchaouen', 'Agadir', 'Fès'];

const EVENT_TYPES = [
  { value: '',          i18nKey: 'hero.typeAll' },
  { value: 'Event',     i18nKey: 'hero.typeTrip' },
  { value: 'Activitie', i18nKey: 'hero.typeActivity' },
  { value: 'Sejour',    i18nKey: 'hero.typeStay' },
];

function CityCombobox({ value, onChange, cities, placeholder }) {
  const [query, setQuery] = useState('');
  const filtered = query === ''
    ? cities
    : cities.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <Combobox value={value} onChange={onChange} nullable>
      <div className="relative">
        <div className="flex items-center gap-2.5 ps-4 pe-3 h-12 rounded-xl bg-white border border-slate-200 hover:border-slate-300 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/15 transition">
          <MapPinIcon className="w-4 h-4 text-ink-subtle shrink-0" />
          <Combobox.Input
            className="flex-1 min-w-0 text-sm font-medium text-ink placeholder:text-ink-subtle bg-transparent outline-none"
            displayValue={(v) => v?.name || ''}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            autoComplete="off"
          />
        </div>
        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0" afterLeave={() => setQuery('')}>
          <Combobox.Options className="absolute start-0 end-0 mt-2 max-h-72 overflow-auto bg-white border border-slate-200 rounded-xl shadow-elevated p-1 z-50 focus:outline-none">
            {filtered.length === 0 && (<div className="px-3 py-2 text-sm text-ink-subtle">—</div>)}
            {filtered.slice(0, 50).map((c) => (
              <Combobox.Option key={c.name} value={c}
                className={({ active }) => cn('flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer', active && 'bg-surface-subtle')}>
                {({ selected }) => (
                  <>
                    {selected && <CheckIcon className="w-4 h-4 text-brand-500 shrink-0" />}
                    <span className={cn('truncate', selected ? 'text-brand-600 font-semibold' : 'text-ink')}>{c.name}</span>
                  </>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
}

function TypeListbox({ value, onChange, placeholder, t }) {
  const current = EVENT_TYPES.find((x) => x.value === value) || EVENT_TYPES[0];
  return (
    <Listbox value={value} onChange={onChange}>
      <div className="relative">
        <Listbox.Button className="flex items-center gap-2.5 ps-4 pe-3 h-12 w-full rounded-xl bg-white border border-slate-200 hover:border-slate-300 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/15 transition text-start">
          <Squares2X2Icon className="w-4 h-4 text-ink-subtle shrink-0" />
          <span className={cn('flex-1 text-sm font-medium truncate', value ? 'text-ink' : 'text-ink-subtle')}>
            {value ? t(current.i18nKey) : placeholder}
          </span>
          <ChevronUpDownIcon className="w-4 h-4 text-ink-subtle shrink-0" />
        </Listbox.Button>
        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
          <Listbox.Options className="absolute start-0 end-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-elevated p-1 z-50 focus:outline-none">
            {EVENT_TYPES.map((opt) => (
              <Listbox.Option key={opt.value || 'all'} value={opt.value}
                className={({ active }) => cn('flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer', active && 'bg-surface-subtle')}>
                {({ selected }) => (
                  <>
                    <span className={cn(selected ? 'text-brand-600 font-semibold' : 'text-ink')}>{t(opt.i18nKey)}</span>
                    {selected && <CheckIcon className="w-4 h-4 text-brand-500" />}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

/**
 * Premium pill-badge at the top of the hero.
 * Uses BOTH brand colors:
 *   - white shell with subtle border,
 *   - tiny yellow dot + yellow sparkle icon as accent,
 *   - brand-blue text.
 */
function HeroBadge({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 ps-1 pe-2.5 py-0.5 rounded-full bg-white border border-slate-200/80 shadow-card">
      <span className="relative inline-flex items-center justify-center w-4 h-4 rounded-full bg-accent-500">
        <SparklesIcon className="w-2.5 h-2.5 text-brand-900" strokeWidth={2.5} />
        <span className="absolute inset-0 rounded-full bg-accent-500 animate-ping opacity-40" />
      </span>
      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-700">
        {children}
      </span>
    </span>
  );
}

/**
 * Three small overlapping rounded thumbnails + a yellow brand disc behind them.
 * Sits inline-right on desktop, hidden on mobile.
 */
/**
 * Single image that shows a shimmer skeleton until the photo finishes loading,
 * then fades in. Prevents the "pop-in" flash on slow networks.
 */
// Inline yellow accent — emphasises one key word in the subtitle.
function YellowHighlight({ children }) {
  return <span className="font-semibold text-accent-500">{children}</span>;
}

/**
 * Each "slot" of the V — same UI as before (position + rotation + border + shadow)
 * but cross-fades between two stacked <img>s as the slide index advances.
 */
function SlideSlot({ wrapperClass, slot }) {
  return (
    <div className={wrapperClass}>
      {slot.map((src, i) => (
        <FadeInImage
          key={src.url}
          src={src.url}
          alt={src.alt}
          eager={i === 0}
          active={src.active}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ))}
    </div>
  );
}

function InlineImageStack() {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const intervalRef = useRef(null);
  const slideCount = INLINE_SLIDES.length;

  // Auto-advance every SLIDE_INTERVAL_MS
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % slideCount);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [slideCount]);

  // Pause on hover, resume on leave
  const pause = () => clearInterval(intervalRef.current);
  const resume = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % slideCount);
    }, SLIDE_INTERVAL_MS);
  };

  // For each of the 3 trio slots, build an array of {url, alt, active} across all slides
  const slot = (slotIdx) =>
    INLINE_SLIDES.map((slide, i) => ({
      url: slide[slotIdx].img,
      alt: slide[slotIdx].alt,
      active: i === index,
    }));

  return (
    <div
      className="relative w-full h-full min-h-[520px] flex items-center justify-center"
      onMouseEnter={pause}
      onMouseLeave={resume}
    >
      {/* Yellow blurred halo behind the trio.
          Kept narrower than the 420px card stage and softened: at 32rem/40%
          with blur-3xl it overhung the cards on both sides and its edge read
          as a solid shape touching the photos instead of a glow. */}
      <div
        aria-hidden
        className="pointer-events-none absolute h-64 w-[22rem] rounded-[50%] bg-accent-500/30 blur-[80px]"
      />

      <div className="relative z-10 flex items-center justify-center" style={{ width: '420px', height: '420px' }}>
        {/* Direction-agnostic positioning: physical left/right so the V stays
            symmetric in RTL (start/end would flip the layout). */}
        {/* Left — leans \ */}
        <SlideSlot
          slot={slot(0)}
          wrapperClass="group absolute -left-8 top-20 w-44 h-56 lg:w-48 lg:h-60 rounded-2xl overflow-hidden border-[5px] border-white shadow-elevated rotate-[-7deg] z-10"
        />
        {/* Middle — | (straight, tallest, on top) */}
        <SlideSlot
          slot={slot(1)}
          wrapperClass="group absolute left-1/2 -translate-x-1/2 top-0 w-48 h-72 lg:w-52 lg:h-80 rounded-2xl overflow-hidden border-[5px] border-white shadow-elevated z-20"
        />
        {/* Right — leans / */}
        <SlideSlot
          slot={slot(2)}
          wrapperClass="group absolute -right-8 top-20 w-44 h-56 lg:w-48 lg:h-60 rounded-2xl overflow-hidden border-[5px] border-white shadow-elevated rotate-[7deg] z-10"
        />

        {/* Floating rating card — centered regardless of direction */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-30 bg-white rounded-full ps-3 pe-4 py-1.5 shadow-elevated border border-slate-100 flex items-center gap-2 hover:scale-105 transition-transform duration-500 whitespace-nowrap">
          <div className="flex text-accent-500">
            {[1,2,3,4,5].map((i) => <StarSolid key={i} className="w-3.5 h-3.5" />)}
          </div>
          <span className="text-sm font-bold text-ink">4,9</span>
          <span className="text-xs text-ink-subtle font-medium whitespace-nowrap">· {t('hero.ratingCount', '2 300 avis')}</span>
        </div>

      </div>
    </div>
  );
}

export default function Hero() {
  const { t } = useTranslation();
  const history = useHistory();
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState(null);
  const [type, setType] = useState('');

  useEffect(() => {
    let alive = true;
    API.CitiesDepart()
      .then((data) => alive && setCities(Array.isArray(data) ? data : (data?.data || [])))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    const params = new URLSearchParams();
    if (city?.name) params.set('city', city.name);
    if (type) params.set('type', type);
    history.push(`/events${params.toString() ? `?${params}` : ''}`);
  };

  const handleQuickCity = (name) => {
    setCity({ name });
    history.push(`/events?city=${encodeURIComponent(name)}`);
  };

  return (
    <section className="relative bg-surface overflow-hidden">
      <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10 lg:pt-12 pb-4 sm:pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-start">

          {/* LEFT — copy & search.
              Centered on mobile (better hierarchy on narrow screens), left-aligned on lg+ */}
          <div className="pt-0 lg:pt-12 text-center lg:text-start">
            {/* Headline — fluid sizing so it fits mobile viewports.
                text-balance avoids ugly orphan-word wraps (e.g. a single word
                stranded on its own line) by rebalancing line lengths. */}
            <h1 className="font-display font-extrabold text-ink leading-[1.15] tracking-tight text-[1.75rem] sm:text-4xl lg:text-5xl">
              <span className="block text-balance">{t('home.heroTitle', 'Vivez des expériences inoubliables')}</span>
              <span className="inline-block text-brand-500 mt-1.5">
                {t('home.heroTitleAccent', 'au Maroc.')}
              </span>
            </h1>

            <p className="mt-6 text-sm sm:text-base lg:text-lg text-ink-muted max-w-xl leading-relaxed">
              <Trans
                i18nKey="home.heroSubtitle"
                t={t}
                defaults="Réservez en quelques clics des activités, voyages et sorties <yellow>authentiques</yellow> partout au Maroc."
                components={{
                  yellow: <YellowHighlight />,
                }}
              />
            </p>

            {/* Search row */}
            <form onSubmit={handleSearch} className="mt-10 grid grid-cols-1 sm:grid-cols-[1.3fr_1fr_auto] gap-2 max-w-2xl">
              <CityCombobox
                value={city}
                onChange={setCity}
                cities={cities}
                placeholder={t('hero.cityPlaceholder', 'Où voulez-vous aller ?')}
              />
              <TypeListbox
                value={type}
                onChange={setType}
                placeholder={t('hero.typePlaceholder', "Type d'expérience")}
                t={t}
              />
              <button
                type="submit"
                className="h-12 px-6 rounded-xl font-semibold whitespace-nowrap bg-brand-500 text-white hover:bg-brand-600 transition-colors shadow-card flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
              >
                <MagnifyingGlassIcon className="w-4 h-4" />
                <span>{t('home.searchButton', 'Rechercher')}</span>
              </button>
            </form>

            {/* Quick-city chips — horizontal scroller on mobile (one row, swipe),
                wraps freely on lg+ */}
            <div className="mt-5 -mx-4 sm:mx-0 max-w-2xl">
              <div className="flex items-center gap-2 overflow-x-auto px-4 sm:px-0 pb-1 lg:flex-wrap lg:overflow-visible scroll-row snap-x snap-mandatory">
                {QUICK_CITIES.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => handleQuickCity(name)}
                    className="shrink-0 snap-start text-xs font-medium px-3 py-1.5 rounded-full bg-surface-subtle text-ink-muted hover:bg-accent-500 hover:text-brand-900 transition-colors whitespace-nowrap"
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats — 3 columns, number on top, label below.
                Centered on mobile, left-aligned on desktop. "cities in Morocco" stays on one line. */}
            <dl className="mt-8 grid grid-cols-3 gap-2 sm:gap-4 max-w-md mx-auto lg:mx-0">
              <div className="text-center lg:text-start">
                <dd dir="ltr" className="font-display font-extrabold text-ink text-xl sm:text-2xl whitespace-nowrap">1,200+</dd>
                <dt className="mt-0.5 text-[10px] sm:text-xs text-ink-muted whitespace-nowrap">
                  {t('hero.statExperiences', 'expériences')}
                </dt>
              </div>
              <div className="text-center lg:text-start border-s border-slate-200 ps-2 sm:ps-4">
                <dd dir="ltr" className="font-display font-extrabold text-ink text-xl sm:text-2xl">41</dd>
                <dt className="mt-0.5 text-[10px] sm:text-xs text-ink-muted whitespace-nowrap">
                  {t('hero.statCities', 'villes au Maroc')}
                </dt>
              </div>
              <div className="text-center lg:text-start border-s border-slate-200 ps-2 sm:ps-4">
                <dd dir="ltr" className="font-display font-extrabold text-ink text-xl sm:text-2xl">300+</dd>
                <dt className="mt-0.5 text-[10px] sm:text-xs text-ink-muted whitespace-nowrap">
                  {t('hero.statPartners', 'partenaires')}
                </dt>
              </div>
            </dl>
          </div>

          {/* RIGHT — small inline images stack (desktop only) */}
          <div className="hidden lg:block">
            <InlineImageStack />
          </div>
        </div>
      </div>
    </section>
  );
}
